import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
    LayoutDashboard,
    User,
    HeartHandshake,
    Globe2,
    Newspaper,
    FolderOpen,
    Eye,
    BarChart3,
    Settings,
    LogOut,
} from "lucide-react";
import TierBadge from "./TierBadge";
import Logo from "./Logo";
import AssoCompletionWidget from "./AssoCompletionWidget";

/**
 * Iter-27.73 — Sidebar dédiée pour le backoffice asso `/asso-admin`.
 *
 * Inspirée de la sidebar /admin Shariti : carte profil en haut + navigation
 * en sections avec icônes. Le tier badge est read-only (admin Shariti only).
 */
const NAV_ITEMS = [
    { id: "dashboard", label: "Tableau de bord", Icon: LayoutDashboard },
    { id: "space", label: "Mon espace", Icon: User },
    { id: "ambassador_view", label: "Vue ambassadeur", Icon: Eye },
    { id: "gratitude", label: "Remerciements", Icon: HeartHandshake },
    { id: "impact", label: "Histoires d'impact", Icon: Globe2 },
    { id: "news", label: "Actualités", Icon: Newspaper },
    { id: "resources", label: "Ressources", Icon: FolderOpen },
    { id: "analytics", label: "Statistiques", Icon: BarChart3 },
    { id: "settings", label: "Paramètres", Icon: Settings },
];

export default function AssoSidebar({ asso, currentTab, onTab, onLogoutRequest, counts = {}, stats }) {
    const accent = asso?.primary_color || "#EE722E";
    // Iter-30 — Préfère `partner_since` (date custom éditée par l'admin Shariti
    // dans le BO) à `created_at` (timestamp d'insertion en DB). Format attendu:
    // ISO YYYY-MM-DD ou YYYY-MM. Fallback sur created_at sinon.
    const partnerSource = asso?.partner_since || asso?.created_at;
    const formattedCreated = partnerSource
        ? (() => {
              try {
                  // Si format YYYY-MM (sans jour), on ajoute le 01 pour parser
                  const src = /^\d{4}-\d{2}$/.test(partnerSource)
                      ? `${partnerSource}-01`
                      : partnerSource;
                  return new Date(src).toLocaleDateString("fr-FR", {
                      month: "short",
                      year: "numeric",
                  });
              } catch (_) {
                  return null;
              }
          })()
        : null;

    return (
        <aside
            className="hidden lg:flex flex-col w-72 shrink-0 h-screen sticky top-0 border-r border-white/5 bg-shariti-deep-black/95 backdrop-blur-xl"
            data-testid="asso-sidebar"
        >
            {/* Branding mark Shariti en haut — h-[72px] alignée sur le header
                principal de AssoAdmin pour que les deux bordures inférieures
                forment une seule ligne continue. */}
            <div className="h-[72px] px-6 flex items-center border-b border-white/5 shrink-0">
                <div className="flex items-center gap-3">
                    <Logo
                        variant="stamp"
                        className="w-9 h-9 shrink-0 ring-1 ring-white/10"
                    />
                    <div className="leading-tight">
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-white/55">
                            Shariti
                        </p>
                        <p
                            className="text-[10px] font-thin tracking-[0.45em] uppercase"
                            style={{
                                color: "transparent",
                                WebkitTextStroke: "0.6px #F89F63",
                            }}
                        >
                            Academy
                        </p>
                    </div>
                </div>
            </div>

            {/* Carte profil asso */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="m-4 p-4 rounded-3xl relative overflow-hidden"
                style={{
                    background: `linear-gradient(135deg, ${accent}20 0%, ${accent}08 60%, transparent 100%)`,
                    border: `1px solid ${accent}40`,
                }}
                data-testid="asso-profile-card"
            >
                <div className="flex items-center gap-3 mb-3">
                    {asso?.logo_url ? (
                        <div className="w-12 h-12 rounded-xl bg-white p-1.5 shrink-0 flex items-center justify-center shadow-lg">
                            <img
                                src={asso.logo_url}
                                alt={asso.name}
                                className="w-full h-full object-contain"
                            />
                        </div>
                    ) : (
                        <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                            style={{ background: `${accent}30` }}
                        >
                            <User className="w-6 h-6" style={{ color: accent }} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-thin tracking-[0.25em] uppercase text-white/55 leading-none">
                            Backoffice
                        </p>
                        <h2 className="text-sm font-extrabold text-white leading-tight line-clamp-2 mt-0.5">
                            {asso?.name || "—"}
                        </h2>
                    </div>
                </div>
                {asso?.tier && (
                    <div className="mb-3">
                        <TierBadge tier={asso.tier} size="sm" />
                    </div>
                )}
                {formattedCreated && (
                    <dl className="text-[10px] space-y-1 text-white/55 font-mono mb-3">
                        <div className="flex items-center justify-between">
                            <dt>Partenaire depuis</dt>
                            <dd className="text-white/75">{formattedCreated}</dd>
                        </div>
                    </dl>
                )}
                <AssoCompletionWidget
                    asso={asso}
                    stats={stats}
                    accent={accent}
                    onGoToTab={(tabId) => onTab?.(tabId)}
                />
            </motion.div>

            {/* Navigation */}
            <nav className="flex-1 px-3 space-y-1 overflow-y-auto pb-4">
                {NAV_ITEMS.map((item) => {
                    const Icon = item.Icon;
                    const isActive = currentTab === item.id;
                    const count = counts[item.id];
                    return (
                        <button
                            key={item.id}
                            onClick={() => onTab(item.id)}
                            data-testid={`asso-nav-${item.id}`}
                            className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-bold transition-all ${
                                isActive
                                    ? "bg-shariti-orange/15 text-white border border-shariti-orange/40"
                                    : "text-white/55 hover:text-white hover:bg-white/5 border border-transparent"
                            }`}
                        >
                            <Icon
                                className={`w-4 h-4 transition-colors ${
                                    isActive ? "text-shariti-orange-light" : "text-white/45"
                                }`}
                            />
                            <span className="flex-1 text-left">{item.label}</span>
                            {count !== undefined && count > 0 && (
                                <span
                                    className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                                        isActive
                                            ? "bg-shariti-orange/30 text-shariti-orange-light"
                                            : "bg-white/8 text-white/60"
                                    }`}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Logout */}
            <div className="px-3 pb-4 pt-2 border-t border-white/5">
                <button
                    onClick={onLogoutRequest}
                    data-testid="asso-logout-trigger"
                    className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-bold text-white/55 hover:text-red-300 hover:bg-red-500/10 transition-all"
                >
                    <LogOut className="w-4 h-4" />
                    Se déconnecter
                </button>
            </div>
        </aside>
    );
}
